import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileText, GitBranch, Settings, Workflow, ClipboardCheck } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";

import AbsenceRequests from "./AbsenceRequests";
import AbsenceReasons from "./AbsenceReasons";
import ApprovalWorkflows from "./ApprovalWorkflows";

function cn(...inputs) {
  return inputs.filter(Boolean).join(" ");
}

const GestionAbsences = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "requests";
  const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true });

  const tabs = [
    {
      id: "requests",
      label: t("gestionAbsences.tabs.requests", "Demandes"),
      icon: ClipboardCheck,
      description: t("gestionAbsences.tabs.requestsDesc", "Valider, rejeter et enregistrer les absences"),
      color: "blue",
    },
    {
      id: "motifs",
      label: t("gestionAbsences.tabs.motifs"),
      icon: FileText,
      description: t("gestionAbsences.tabs.motifsDesc"),
      color: "blue",
    },
    {
      id: "workflows",
      label: t("gestionAbsences.tabs.workflows"),
      icon: GitBranch,
      description: t("gestionAbsences.tabs.workflowsDesc"),
      color: "purple",
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "requests":  return <AbsenceRequests />;
      case "motifs":    return <AbsenceReasons />;
      case "workflows": return <ApprovalWorkflows />;
      default:          return <AbsenceRequests />;
    }
  };

  const currentTab = tabs.find(t => t.id === activeTab);

  const tabColors = {
    blue: {
      bg: "bg-primary/10", border: "border-primary/40", borderLeft: "border-l-primary",
      text: "text-primary", buttonActive: "bg-primary text-white border-primary",
      buttonHover: "hover:bg-primary/10 hover:border-primary/40 hover:text-primary",
      badgeBg: "bg-primary/20", badgeText: "text-primary",
    },
    purple: {
      bg: "bg-muted", border: "border-border", borderLeft: "border-l-muted-foreground",
      text: "text-muted-foreground", buttonActive: "bg-secondary text-secondary-foreground border-border",
      buttonHover: "hover:bg-accent hover:border-border hover:text-foreground",
      badgeBg: "bg-muted", badgeText: "text-muted-foreground",
    },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold leading-7 sm:text-3xl">
            {t("gestionAbsences.title")}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("gestionAbsences.subtitle")}
          </p>
        </div>

        <Badge variant="outline" className="flex items-center gap-1.5 px-3 py-1.5">
          <Workflow className="h-3.5 w-3.5" />
          <span className="font-medium">{t("gestionAbsences.module")}</span>
        </Badge>
      </div>

      {/* Main Content Card */}
      <Card className="shadow-sm">
        <CardContent className="p-8">
          {/* Navigation Title */}
          <div className="mb-6">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <Settings className="h-5 w-5 text-muted-foreground" />
              {t("gestionAbsences.configTitle")}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t("gestionAbsences.configDesc")}
            </p>
          </div>

          {/* Navigation Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const colors = tabColors[tab.color];

              return (
                <Card
                  key={tab.id}
                  className={cn(
                    "border-2 transition-all hover:shadow-md bg-card",
                    isActive
                      ? cn(colors.borderLeft, "border-l-4 shadow-md", colors.bg)
                      : "border-border hover:border-muted-foreground/30"
                  )}
                >
                  <CardHeader className="p-5">
                    <button
                      onClick={() => setActiveTab(tab.id)}
                      className="w-full text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            "p-3 rounded-xl shadow-sm",
                            isActive ? colors.buttonActive.split(' ')[0] : "bg-muted",
                            isActive ? "text-white" : "text-muted-foreground"
                          )}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-base font-semibold mb-1">
                            {tab.label}
                          </CardTitle>
                          <CardDescription className={cn(
                            "text-sm",
                            isActive ? colors.text : "text-muted-foreground"
                          )}>
                            {tab.description}
                          </CardDescription>
                        </div>
                      </div>
                    </button>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          <Separator className="my-8" />

          {/* Current Section Context Banner */}
          {currentTab && (
            <Card className={cn(
              "border-2 mb-6",
              tabColors[currentTab.color].bg,
              tabColors[currentTab.color].border
            )}>
              <CardHeader className="py-4 px-5">
                <div className="flex items-center gap-4">
                  <div className={cn(
                    "p-3 rounded-xl shadow-sm",
                    tabColors[currentTab.color].buttonActive.split(' ')[0],
                    "text-white"
                  )}>
                    {(() => {
                      const Icon = currentTab.icon;
                      return <Icon className="h-5 w-5" />;
                    })()}
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-base font-semibold flex items-center gap-2 mb-1">
                      {currentTab.label}
                      <Badge variant="outline" className={cn(
                        "text-xs font-medium",
                        tabColors[currentTab.color].badgeBg,
                        tabColors[currentTab.color].badgeText,
                        "border-transparent"
                      )}>
                        {t("gestionAbsences.configTitle")}
                      </Badge>
                    </CardTitle>
                    <CardDescription className={cn("text-sm", tabColors[currentTab.color].text)}>
                      {currentTab.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          )}

          {/* Tab Content */}
          <div className="min-h-[500px]">
            {renderTabContent()}
          </div>
        </CardContent>
      </Card>

      {/* Info Footer */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl" style={{ backgroundColor: "rgba(94,106,210,0.12)" }}>
              <Settings className="h-6 w-6" style={{ color: "#5e6ad2" }} />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-foreground text-base mb-2">
                {t("gestionAbsences.fullTitle")}
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("gestionAbsences.fullDesc")}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                <div className="flex items-center gap-2.5 text-sm text-foreground font-medium">
                  <div className="p-1.5 bg-muted rounded-lg">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <span>{t("gestionAbsences.customReasons")}</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-foreground font-medium">
                  <div className="p-1.5 bg-muted rounded-lg">
                    <GitBranch className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <span>{t("gestionAbsences.flexibleWorkflows")}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GestionAbsences;
